import React, { Component, ReactChild } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import InputGroup from '../inputGroup/InputGroup';

export enum DropdownStyles {
  flat,
}

// tslint:disable-next-line:variable-name
const SDropdownWrap = styled.div`
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 800px) {
    flex-basis: 0;
  }
`;

// tslint:disable-next-line:variable-name
const SDropdown = styled(Select)<{ error: string; style: DropdownStyles; dataAt: string }>(
  ({ theme, error, style }) => ({
    position: 'relative',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    '*': {
      fontFamily: theme.font.family,
    },
    '.dropDown__control': {
      backgroundColor: theme.colors.whiteGray,
      border: `solid ${error ? `${theme.colors.error3} 1.5px` : `${theme.colors.inputBorder} 1px`}`,
      borderRadius: theme.borderRadiusCommon,
      position: 'relative',
      cursor: 'pointer',
      boxShadow: 'none' /* override  */,
      '&:hover': {
        border: `solid ${error ? `${theme.colors.error3} 1.5px` : `${theme.colors.border} 1px`}`,
      },
    },
    '.dropDown__value-container': {
      fontWeight: theme.font.weight.light,
      fontSize: theme.font.size.smallMedium2,
      boxSizing: 'border-box',
      color: theme.colors.grayDark,
      backgroundColor: 'transparent',
      borderWidth: 0,
      padding: `${theme.spacing.mediumLarger}px ${theme.spacing.large}px`,
      div: {
        margin: 0,
        padding: 0,
        fontWeight: theme.font.weight.light,
        fontSize: theme.font.size.smallMedium2,
        color: theme.colors.grayDark,
      },
      input: {
        width: '100%',
        appearance: 'none',
        outline: 'none',
        margin: 0,
        padding: 0,
        borderWidth: 0,
        fontSize: theme.font.size.smallMedium2,
      },
    },
    '.dropDown__placeholder': {
      fontWeight: theme.font.weight.light,
      color: `${theme.colors.placeholder} !important`,
      fontSize: theme.font.size.smallMedium2,
      position: 'absolute',
      margin: 0,
      top: 'auto',
      transform: 'initial',
      div: {
        margin: 0,
      },
    },

    // Flat style
    ...(style === DropdownStyles.flat
      ? {
          '.dropDown__menu': {
            margin: 0,
            color: theme.colors.primaryBackground,
            fontWeight: '500 !important',
          },
          '.dropDown__menu-list': {
            padding: 0,
            borderBottomRightRadius: '8px',
            borderBottomLeftRadius: '8px',
          },
          '.dropDown__control--menu-is-open': {
            borderBottom: 'none',
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
          },
          '.dropDown__option': {
            padding: '13px 12px',
          },
          '.dropDown__indicator-separator': {
            display: 'none',
          },
          '.dropDown__single-value': {
            color: `${theme.colors.primaryBackground} !important`,
            fontWeight: '500 !important',
            fontFamily: 'Poppins',
          },
          '.dropDown__control': {
            border: 'none',
            boxShadow: 'none',
            '&:hover': {
              border: 'none',
            },
            '.dropDown__indicator': {
              color: theme.colors.primaryBackground,
            },
          },
          '.dropDown__control--is-disabled .dropDown__indicators': {
            // Remove the expand arrow when disabled
            visibility: 'hidden',
          },
        }
      : {}),
  })
);

export interface IDropdownOptionForSelect<T> {
  label: string;
  value: T;
}

export interface IDropdownProps<T> {
  onChange: (selectedOption: IDropdownOptionForSelect<T>, name: string) => void;
  name: string;
  placeholder?: string;
  value: T;
  dropdownOptions: Array<IDropdownOptionForSelect<T>>;
  dataAt?: string;
  className?: string;
  label?: string | ReactChild;
  tooltipContent?: string;
  error?: string;
  hideSelectedOption?: boolean;
  style?: DropdownStyles;
  isSearchable?: boolean;
  isDisabled?: boolean;
}

export class Dropdown<T> extends Component<IDropdownProps<T>> {
  public handleOnChange = (selectedOption: IDropdownOptionForSelect<T>) => {
    this.props.onChange(selectedOption, this.props.name);
  };

  private filterOption = ({ value }) => {
    const selectedOption = this.getSelectedOption();
    return value != selectedOption?.value;
  };

  private getSelectedOption = () => {
    const { dropdownOptions } = this.props;

    let selectedOption = null;

    if (this.props.value && dropdownOptions?.length) {
      selectedOption = this.props.dropdownOptions.find((item) => {
        return item.value === this.props.value;
      });
    }

    return selectedOption;
  };

  public render() {
    const {
      hideSelectedOption,
      dropdownOptions,
      style,
      className,
      dataAt,
      label,
      error,
      isSearchable,
      isDisabled = false,
    } = this.props;
    const placeholder = this.props.placeholder || this.props.name.replace(/^\w/, (c) => c.toUpperCase());
    const selectedOption = this.getSelectedOption();

    return (
      <SDropdownWrap {...{ 'data-at': dataAt, className }}>
        <InputGroup
          {...{ className, dataAt: `${dataAt}-group`, label, error: error || '' }}
          tooltipContent={this.props.tooltipContent || ''}
        >
          <SDropdown
            {...{
              classNamePrefix: 'dropDown',
              style,
              isSearchable,
              isDisabled,
            }}
            dataAt={`${this.props.dataAt}-select`}
            options={dropdownOptions}
            placeholder={placeholder}
            onChange={this.handleOnChange}
            value={selectedOption}
            error={this.props.error || ''}
            filterOption={hideSelectedOption ? this.filterOption : undefined}
          />
        </InputGroup>
      </SDropdownWrap>
    );
  }
}
